import { useId } from "react";
import { t } from "../i18n/t";

/**
 * §3 empty state: centred white block, radius 16, padding 56×40; an optional icon row (56 px tiles, decorative);
 * the 22/600 title as an `h2` the section is labelled by; muted body (max 480); optional numbered step cards
 * (three columns, sunken; the next step gets the selected-card ring); then the optional primary action.
 */
export type EmptyStateStep = {
  label: string;
  sub: string;
  state: "done" | "next" | "todo";
};

export function EmptyState({
  title,
  body,
  icon,
  steps,
  action,
}: {
  title?: string;
  body?: string;
  /** Decorative icon content (tiles, connectors), rendered in an `aria-hidden` row. */
  icon?: React.ReactNode;
  steps?: readonly EmptyStateStep[];
  /** Typically the primary `<Button>` that gets the operator unstuck. */
  action?: React.ReactNode;
}): JSX.Element {
  const titleId = useId();
  return (
    <section className="empty" aria-labelledby={titleId}>
      {icon === undefined ? null : (
        <div className="empty__icons" aria-hidden="true">
          {icon}
        </div>
      )}
      <div className="empty__text">
        <h2 className="empty__title" id={titleId}>
          {title ?? t("common.empty_title")}
        </h2>
        <p className="empty__body">{body ?? t("common.empty_body")}</p>
      </div>
      {steps === undefined ? null : (
        <ol className="empty__steps">
          {steps.map((step, index) => (
            <li key={step.label} className={`empty__step empty__step--${step.state}`}>
              <span className="empty__step-index">{emptyStepIndex(index, step.state)}</span>
              <span className="empty__step-label">{step.label}</span>
              <span className="empty__step-sub">{step.sub}</span>
            </li>
          ))}
        </ol>
      )}
      {action === undefined ? null : action}
    </section>
  );
}

function emptyStepIndex(index: number, state: EmptyStateStep["state"]): string {
  const number = String(index + 1).padStart(2, "0");
  if (state === "done") return `${number} · ${t("stage.done")}`;
  if (state === "next") return `${number} · ${t("common.next")}`;
  return number;
}
